import { hrefForNext } from "@/utils/nextHref";
import { useMutation } from "@apollo/client/react";
import { parseNextPath } from "@/utils/nextPath";
import {
  nextFromState,
  takeOAuthState,
  type OAuthProvider,
} from "@repo/components/src/shared/login/oauthState";
import { logIn } from "@repo/graphql/client";
import { Oauth2LoginDocument } from "@repo/graphql/documents/user/mutation.generated";
import { MyUserCenterDocument } from "@repo/graphql/documents/user/query.generated";
import dayjs from "dayjs";
import { router, useLocalSearchParams, type Href } from "expo-router";
import { useEffect, useRef, useState } from "react";

/**
 * Finishes a web OAuth login (the provider redirects back with the token in the URL
 * hash). The login is only honoured when the returned `state` is the one this tab sent
 * (see `useWebOAuthLogin`): the callback URL is public, and a link built by someone else
 * must never sign this browser into their account — nor carry its `next` along.
 *
 * `href` is where to go once the new session is stored, null until then; redirecting
 * earlier would act on whichever session the browser still held.
 */
const useOAuthCallback = (provider: OAuthProvider) => {
  const searchParams = useLocalSearchParams<Record<string, string>>();
  const [href, setHref] = useState<Href | null>(null);
  const nextRef = useRef<Href | null>(null);
  const started = useRef(false);

  const [oauth, { error }] = useMutation(Oauth2LoginDocument, {
    refetchQueries: [MyUserCenterDocument],
    awaitRefetchQueries: true,
    onCompleted: async (data, options) => {
      const { nextPath, ...session } = data.user.oauth2Login;
      await logIn(session, options?.client);
      // `nextPath` is set when this sign-in verified an email sign-up and published its
      // campaign: that page wins over the claim page the `state` asked for, which would
      // find no campaign left to claim. Only allow-listed pages are honoured.
      const opened = parseNextPath(nextPath);
      setHref(opened ? hrefForNext(opened) : nextRef.current);
    },
  });

  useEffect(() => {
    if (started.current) return;
    const hash = searchParams["#"];
    if (!hash) {
      router.replace("/");
      return;
    }
    started.current = true;
    const params = new URLSearchParams(hash);
    const state = params.get("state");
    const accessToken = params.get("access_token");
    const expiresIn = params.get("expires_in");
    if (!accessToken || !expiresIn || !takeOAuthState(provider, state)) {
      router.replace("/");
      return;
    }
    // Where to land: the sign-up funnel asks for its claim page through the
    // `state`; anything not on the allow-list falls back to the app.
    nextRef.current = hrefForNext(parseNextPath(nextFromState(state)));
    void oauth({
      variables: {
        connectAccountInput: {
          accessToken,
          permissions: [],
          applicationId: "",
          expiresIn: Number(expiresIn),
          issuedAt: dayjs().unix(),
          provider,
        },
      },
    });
  }, [searchParams, provider, oauth]);

  return { href, error };
};

export default useOAuthCallback;
